import { ElementType } from 'react';
import { Tooltip } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';

import { approvalAttribution } from './RecordStatusChip';
import { EsgRecordStatus } from '../../../types/syntiro_honey';

/**
 * One glyph per status, for the GRID only — the detail surfaces keep the labelled
 * {@link RecordStatusChip}, where there is room for words and the reader is
 * studying one record rather than scanning three hundred.
 *
 * Colour is spent only on the two TERMINAL states; everything in progress is
 * neutral, so the row's single traffic light stays the confidence score. Shape is
 * what distinguishes the neutral three from each other.
 */
const STATUS_ICON: Record<
  EsgRecordStatus,
  { label: string; color: 'action' | 'success' | 'error'; Icon: ElementType }
> = {
  PENDING_REVIEW: { label: 'Pending review', color: 'action', Icon: ScheduleIcon },
  NEEDS_DISAMBIGUATION: {
    label: 'Needs disambiguation — pick the right metric',
    color: 'action',
    Icon: HelpOutlineIcon
  },
  FLAGGED: {
    label: 'Flagged — validation raised something',
    color: 'action',
    Icon: FlagOutlinedIcon
  },
  APPROVED: { label: 'Approved', color: 'success', Icon: CheckCircleIcon },
  REJECTED: { label: 'Rejected', color: 'error', Icon: CancelIcon }
};

interface RecordStatusIconProps {
  /** The review status to render. */
  status: EsgRecordStatus;
  /** Approver SSO uid (the attribution fallback when no name resolved), or null. */
  approvedById?: string | null;
  /** Approval timestamp (ISO 8601), or null. */
  approvedAt?: string | null;
  /** Approver's resolved display name, preferred over the uid in the attribution. */
  approvedByName?: string | null;
}

/**
 * Compact review-status indicator for the records grid: a glyph carrying its
 * meaning as both a tooltip and an accessible name, so the status never depends on
 * recognising an icon (or on seeing colour) alone.
 *
 * Replaces a ~170px labelled chip. That width went to the Trust column, which was
 * clipping its confirm action. The approver attribution that used to render as a
 * caption under the chip moves into this tooltip; it remains in full on the
 * record's detail modal and in its history.
 * @param {RecordStatusIconProps} props The status and optional approver attribution.
 * @returns {JSX.Element}
 */
export default function RecordStatusIcon({
  status,
  approvedById = null,
  approvedAt = null,
  approvedByName = null
}: RecordStatusIconProps) {
  const { label, color, Icon } = STATUS_ICON[status];
  const attribution =
    status === 'APPROVED' ? approvalAttribution(approvedById, approvedAt, approvedByName) : null;
  // An approved record names its approver here, since the caption that used to
  // carry it under the chip has no room in an icon-width column.
  const title = attribution ?? label;
  return (
    <Tooltip title={title}>
      {/* span carries the ref Tooltip needs and keeps the icon inline */}
      <span style={{ display: 'inline-flex' }}>
        {/* titleAccess makes the label the icon's accessible NAME — an svg alone is
            announced as nothing, and colour must never be the only carrier. */}
        <Icon color={color} sx={{ fontSize: 20 }} titleAccess={title} />
      </span>
    </Tooltip>
  );
}
